/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable, Output, EventEmitter, Directive } from '@angular/core';
import { ImageType } from '../models/imageType';
import { ImageFile } from '../models/imageFile';
import { FilterParams, getMinutesElapsedBetweenTwoDates, ISelectable, AlphaSortArrayByProperty } from 'starbase-launcher';
import { of, Observable, Subject, BehaviorSubject } from 'rxjs';
import { LookupDataService } from './lookup-data.service';
import { map, filter } from 'rxjs/operators';
import { LU_Keyword } from '../models/keyword';
import { CallNumberModel } from '../models/callNumberModel';
import { ImageService } from './image.service';
import { RelatedImageSetViewDataModel } from '../models/relatedImageSetViewDataModel';

const GALLERY_IMG_TYPE = 'galleryImgTypeStore';
const STALE_IMG_DATA_MINUTES = 5;
export const DEFAULT_RPP_SETTING = 35;

@Directive()
@Injectable({
  providedIn: 'root'
})
export class GalleryTypeService {
  @Output() galleryTypeEmitter: EventEmitter<ImageType> = new EventEmitter<ImageType>();
  @Output() repairLibraryImages: EventEmitter<FilterParams<ImageFile>> = new EventEmitter<FilterParams<ImageFile>>();
  @Output() libraryImagesRepaired: EventEmitter<any> = new EventEmitter<any>();

  private filteredImageIds: Array<number> = new Array<number>();
  private libraryRelatedSetImages: ImageFile[] = [];
  private libraryImages: ImageFile[] = [];
  private libraryRelatedImageSetRecords: RelatedImageSetViewDataModel[] = [];
  private rsRecordsLastRetrievedTime: Date;
  private lastLoadMoreClickedCount: number;
  private currentLibraryFilters: FilterParams<ImageFile> = null;
  private currentLedgerFilters: any = null;
  private currentKeywordFilter = '';
  private currentRelatedSetNameFilter = '';
  private currentRelatedSetUIDFilter = 0;
  private currentCallNoSearch: CallNumberModel;
  private galleryType: ImageType = null;
  private imagesLastRetrievedTime: Date;
  private viewMode: string;
  private lastViewedRecord: ImageFile;
  private lastViewedRecordIsRelatedResult: boolean;
  private filterOptions: { entity: string; data: ISelectable[] }[] = [];
  private _prePrintedMode = undefined; // set gallery to show military pre-printed forms
  private _ledgerSetsMode = undefined; // set gallery to show ledger sets
  private _civilianResearchMode = undefined; // set gallery to Civilian Research mode
  private _prePrintedTypes: ImageType[] = [];
  private _libraryFilterBarWidth = 245; // px
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public libraryFilterBarWidth$ = new BehaviorSubject<number>(this._libraryFilterBarWidth);
  private _runIndividualSearchTrigger$ = new Subject<string>();
  private _hasReloaded = false;

  private libraryLedgerSets: RelatedImageSetViewDataModel[] = [];
  private ledgerSetsLastRetrievedTime: Date;



  constructor(private lookupSvc: LookupDataService, private imageSvc: ImageService) { }

  get CountOfFilters(): number {
    // eslint-disable-next-line max-len
    return this.currentLibraryFilters ? this.currentLibraryFilters.filters.length + this.currentLibraryFilters.orClusterFilters.length - this.CountOfCallNumberFilters : 0;
  }
  get CountOfCallNumberFilters(): number {
    // eslint-disable-next-line max-len
    return this.currentLibraryFilters && this.currentLibraryFilters.filters.some(f =>  (f[0] as string).startsWith('CallNumberL') || f[0] === 'SourceUID') ? this.currentLibraryFilters.filters.filter(f => (f[0] as string).startsWith('CallNumberL') || f[0] === 'SourceUID').length : 0;
  }
  get CountOfFiltersIgnoreImageType(): number {
    if ((this.getGalleryType().uid !== 0 || this.prePrintedMode) ||
    (this.CivilianResearchMode && (this.currentLibraryFilters.filters.some(filterName => filterName[0] === 'TypeOfImageId')))
    ) {
      return this.CountOfFilters - 1; // remove one to deal with the "type of image" filter that's applied
    } else {
      return this.CountOfFilters;
    }
  }
  get HasActiveSearch(): boolean {
    return this.currentLibraryFilters && this.currentLibraryFilters.searchPhrase !== null
          && this.currentLibraryFilters.searchPhrase !== undefined
          && this.currentLibraryFilters.searchPhrase.length > 0;
  }
  get CountOfLibImages(): number {
    if (getMinutesElapsedBetweenTwoDates(new Date(this.imagesLastRetrievedTime), new Date()) > STALE_IMG_DATA_MINUTES) {
      console.warn('Image data was stale. Discarding.');
      return 0;
    }
    return this.libraryImages.length;
  }
  public get CivilianResearchMode() {
    if (this._civilianResearchMode === undefined) {
      return this.retrieveGalleryState().civResearch;
    } else {
      return this._civilianResearchMode;
     }
  }
  public set CivilianResearchMode(val: boolean) {
    if (val === true) {
      if (this.getGalleryType().uid === 0 || this.getGalleryType().typeOfImage === 'All') {
        this._civilianResearchMode = true;
        this.PrePrintedMode(false);
        this._ledgerSetsMode = false;
      } else {
        throw new Error('Gallery cannot enter civilian research mode when not viewing ALL libraries.');
      }
    } else {
      this._civilianResearchMode = false;
    }
  }

  public setLibraryLedgerSets(ledgers: RelatedImageSetViewDataModel[]): void {
    this.libraryLedgerSets = ledgers;
    this.ledgerSetsLastRetrievedTime = new Date();
  }

  public getLibraryLedgerSets(): RelatedImageSetViewDataModel[] {
    if (getMinutesElapsedBetweenTwoDates(this.ledgerSetsLastRetrievedTime, new Date()) >= STALE_IMG_DATA_MINUTES) {
      console.warn('Ledger data was stale. Discarding.');
      return [];
    }
    return this.libraryLedgerSets;
  }

  public clearLibraryLedgerSets(): void {
    this.libraryLedgerSets = [];
  }

  public get LedgerSetsMode() {
    if (this._ledgerSetsMode === undefined) {
      const state = this.retrieveGalleryState();
      return state && state.ledgerSets ? state.ledgerSets : false;
    } else {
      return this._ledgerSetsMode;
    }
  }

  public set LedgerSetsMode(val: boolean) {
  console.log('LedgerSetsMode setter called with:', val);
  if (val === true) {
    if (this.getGalleryType().uid === 0 || this.getGalleryType().typeOfImage === 'All') {
      this._ledgerSetsMode = true;
      this._prePrintedMode = false;
      this._civilianResearchMode = false;
      
      // Store state immediately
      this.storeGalleryState({ 
        type: this.getGalleryType(), 
        prePrinted: false, 
        civResearch: false, 
        ledgerSets: true 
      });
      
      const filters = this.getCurrentLibraryFilters(null, false);
      filters.rpp = DEFAULT_RPP_SETTING;
      if (this.currentLibraryFilters) {
        filters.replaceFilter('TypeOfImageId', '0');
        filters.removeFromFilter('TypeOfImageId', '0');
        this.setCurrentLibraryFilters(filters);
        this.clearCurrentFilterOptions();
      }
    } else {
      throw new Error('Gallery cannot enter ledgers mode when not viewing ALL libraries.');
    }
  } else {
    this._ledgerSetsMode = false;
    
    // Store state immediately
    this.storeGalleryState({ 
      type: this.getGalleryType(), 
      prePrinted: this._prePrintedMode || false, 
      civResearch: this._civilianResearchMode || false, 
      ledgerSets: false 
    });
    
    const filters = this.getCurrentLibraryFilters(null, true);
    filters.rpp = DEFAULT_RPP_SETTING;
    if (this.currentLibraryFilters && !this.CivilianResearchMode && !this.prePrintedMode) {
      filters.replaceFilter('TypeOfImageId', '0');
      filters.removeFromFilter('TypeOfImageId', '0');
      this.setCurrentLibraryFilters(filters);
      this.clearCurrentFilterOptions();
    }
  }
}

  /** Returns all the previously-retrieved ImageTypes for Military Pre-printed Forms mode, including their current `selected` state.
   *  This array will be sorted alpha-numerically. */
  public get PrePrintedTypes(): Observable<ImageType[]> {
    const hasReloaded = this._hasReloaded;
    if (this._prePrintedTypes.length) {
      return of(this._prePrintedTypes);
    } else {
      return this.lookupSvc.getAllData(this.lookupSvc.imageTypesUrl).pipe(
        map(t => t.filter((tt: ImageType) => tt.prePrintedFormType === true)),
        map((t: ImageType[]) => {
          let selectedPrePrintedTypes = null;
          if(this.currentLibraryFilters.filters && this.currentLibraryFilters.filters.length > 0){
          selectedPrePrintedTypes = this.currentLibraryFilters.filters[0][1].split('|').map(x => parseInt(x,10));
        }
          t.map(tt => {
            if (hasReloaded && selectedPrePrintedTypes !== null){
              tt.selected = selectedPrePrintedTypes.some(type => type === tt.uid);
              return tt;
            } else {
              tt.selected = true;
              return tt;
            }
            });
            this._hasReloaded = false;
            return AlphaSortArrayByProperty(t, 'typeOfImage', true);
          }
        ));
    }
  }
  public get prePrintedMode() {
    if (this._prePrintedMode !== undefined) {
      return this._prePrintedMode;
    } else {
      const ppm = this.retrieveGalleryState().prePrinted;
      this.setPrePrintedMode(ppm).subscribe();
      return ppm;
    }
  }

  public get hasReloaded() {
    return this._hasReloaded;
  }

  public reloadComplete() {
    this._hasReloaded = false;
  }

  public set libraryFilterBarWidth(val: number) {
    this._libraryFilterBarWidth = Math.abs(val);
    this.libraryFilterBarWidth$.next(this._libraryFilterBarWidth);
  }
  public setPrePrintedMode(val: boolean): Subject<boolean> {
    const result = new Subject<boolean>();
    try {
      this.PrePrintedMode(val);
      this._prePrintedTypes = [];
      if (val) {
        this.PrePrintedTypes.subscribe(t => {
          this._prePrintedTypes = t;
          result.next(true);
        },
          e => {
            console.error('Could not load image type data. ', e);
            result.next(false);
          });
      } else {
        result.next(false);
      }
    } catch (e) {
      console.error(e);
      result.next(false);
    }
    return result;
  }

  public togglePrePrintedType(id: number, onOff: boolean) {
    if (id === 0) {
      this._prePrintedTypes.forEach(a => a.selected = onOff);
    } else {
      this._prePrintedTypes.find(a => a.uid === id).selected = onOff;
    }
    this.currentLibraryFilters.page = 1;
  }

  /** Used for priming for a particular image type base filter
   *
   * @param type The ImageType object to use when setting the base filter.
   * @note The library cache is cleared when this method is called */
  public setGalleryType(type: ImageType, keepFilters = false) {
    this.galleryType = type;
    this.galleryTypeEmitter.emit(this.galleryType);
    this.storeGalleryState({ type, prePrinted: this.prePrintedMode, civResearch: this.CivilianResearchMode, ledgerSets: this._ledgerSetsMode });
      if (!keepFilters) {
      this.currentLibraryFilters = null; // reset this because we have changed image types
    }
    this.clearLibraryImages(); // reset this so we don't load the wrong image type
    this.imageSvc.clearFilterCache();
    this.clearCurrentFilterOptions();
  }

  public getGalleryType(): ImageType {
    let theType = (this.galleryType === null) ? this.retrieveGalleryState().type : this.galleryType;
    if (!theType) {
      this.setGalleryType(new ImageType());
      theType = this.galleryType;
    }
    return theType;
  }

  public writeGalleryPageText(): string {
    const typeName = (!this.getGalleryType().typeOfImage) ? '' : this.getGalleryType().typeOfImage.replace('_', ' ');
    if (this.CivilianResearchMode) {
      return 'Civilian Research';
    } else if (this.prePrintedMode) {
      return 'Military Forms';
    } else if (this.LedgerSetsMode) {
      return 'Ledgers';
    }
    return typeName;
  }


  public setFilteredImageIds(idArray: ImageFile[]): void {
    this.filteredImageIds = idArray.map(({ uid }) => uid);
  }
  public getFilteredImageIds(): Array<number> {
    return this.filteredImageIds;
  }

  public getLastResultsPerPageSetting(defaultRpp: number): number {
    if (!this.currentLibraryFilters) {
      return defaultRpp;
    } else {
      return this.currentLibraryFilters.rpp;
    }
  }
  public getLibraryImages(rppOverride?: number): ImageFile[] {
    if (getMinutesElapsedBetweenTwoDates(this.imagesLastRetrievedTime, new Date()) >= STALE_IMG_DATA_MINUTES) {
      console.warn('Image data was stale. Discarding.');
      return [];
    } else {
      const rpp = rppOverride ? rppOverride : this.currentLibraryFilters.rpp;
      return this.libraryImages.slice(0, rpp);
    }
  }
  public setLibraryImages(lib: ImageFile[]): void {
    this.libraryImages = lib;
    this.setFilteredImageIds(lib);
    this.imagesLastRetrievedTime = new Date(); // refresh the last retrieval date
  }
  /** Empty the naive cache of gallery images. */
  public clearLibraryImages(): void {
    this.libraryImages = [];
  }

  /** Updates record in the cache. Does not affect server data */
  public updateLibraryImage(updated: ImageFile) {
    if (this.libraryImages.length <= 0) {
      return;
    }
    const updateThis = this.libraryImages.findIndex(a => a.uid === updated.uid);
    this.libraryImages[updateThis] = updated;
  }

  public setLibraryRelatedImageSetRecords(rs: RelatedImageSetViewDataModel[], loadMoreCount): void {
    this.lastLoadMoreClickedCount = loadMoreCount;
    this.libraryRelatedImageSetRecords = rs;
    this.rsRecordsLastRetrievedTime = new Date();
  }

  public getLibraryRelatedImageSetRecords() {
    if (getMinutesElapsedBetweenTwoDates(this.rsRecordsLastRetrievedTime, new Date()) >= STALE_IMG_DATA_MINUTES) {
      console.warn('Related Image Set data was stale. Discarding.');
      return [];
    } else {
      return this.libraryRelatedImageSetRecords;
    }
  }

  public get lastRelatedImageSetLoadMoreCount() {
    return this.lastLoadMoreClickedCount;
  }

  public get CountOfRISRecords(): number {
    if (getMinutesElapsedBetweenTwoDates(new Date(this.imagesLastRetrievedTime), new Date()) > STALE_IMG_DATA_MINUTES) {
      console.warn('Image data was stale. Discarding.');
      return 0;
    }
    return this.libraryRelatedImageSetRecords.length;
  }

  /** Returns the `FilterParams` currently in effect on the gallery.
   * If there is not one, a new `FilterParams` instance will be constructed using the current gallery ImageType (or View All).
   *
   * @param rppOverride Overrides the existing FilterParams' `rpp` property. */
  public getCurrentLibraryFilters(rppOverride?: number, isReload?: boolean): FilterParams<ImageFile> {
    // this.setPrePrintedTypes();

    if (this.LedgerSetsMode && this.currentLedgerFilters) {
      const cf = new FilterParams<ImageFile>();
      cf.page = this.currentLedgerFilters.page || 1;
      cf.rpp = rppOverride || this.currentLedgerFilters.rpp || DEFAULT_RPP_SETTING;
      const ledgers = this.getLibraryLedgerSets();
      cf.collection = ledgers as any[];

      if (this.currentLibraryFilters) {
        cf.explicitSearchProperties = this.currentLibraryFilters.explicitSearchProperties;
        cf.filters = this.currentLibraryFilters.filters;
        cf.orClusterFilters = this.currentLibraryFilters.orClusterFilters;
        cf.searchFilters = this.currentLibraryFilters.searchFilters;
        cf.searchPhrase = this.currentLibraryFilters.searchPhrase;
        cf.sortAsc = this.currentLibraryFilters.sortAsc;
        cf.sortFields = this.currentLibraryFilters.sortFields;
        cf.sortStringNumerically = this.currentLibraryFilters.sortStringNumerically;
      }
      
      // Copy readonly fields using Object.assign
      Object.assign(cf, {
        totalRecords: this.currentLedgerFilters.totalRecords || 0,
        totalPages: this.currentLedgerFilters.totalPages || 1
      });
      
      return cf;
    }

    if (isReload && this.currentLibraryFilters == null) {
      this._hasReloaded = true;
      this.currentLibraryFilters = JSON.parse(localStorage.getItem('CurrentFilters'));
      this.currentLibraryFilters.page = 1;
    }
    if (this.currentLibraryFilters == null) {
      this.currentLibraryFilters = new FilterParams();
      if (this.galleryType != null && this.galleryType.uid !== 0) {
        this.currentLibraryFilters.addToFilter('TypeOfImageId', this.galleryType.uid);
      }
    }

    this.currentLibraryFilters.rpp = rppOverride || this.getLastResultsPerPageSetting(DEFAULT_RPP_SETTING);
    return new FilterParams().deserialize(this.currentLibraryFilters) as FilterParams<ImageFile>;
  }

  public setCurrentLedgerFilters(filters: any): void {
    this.currentLedgerFilters = {
      page: filters.page,
      rpp: filters.rpp,
      totalRecords: filters.totalRecords,
      totalPages: filters.totalPages
    };
  }

  public clearLedgerFilters(): void {
    this.currentLedgerFilters = null;
  }

  /** Stores the `FilterParams` in effect for the gallery. */
  public setCurrentLibraryFilters(filters: FilterParams<ImageFile>): void {
    this.currentLibraryFilters = filters;
    this.storeCurrentLibraryFilters();
  }

  public storeCurrentLibraryFilters(): void {
    if (this.currentLibraryFilters ){
      localStorage.setItem('CurrentFilters', JSON.stringify(this.currentLibraryFilters));
    }
  }
  /** Updates the filter options and selected states for the gallery filter sidebar.
   *
   * @param entity The filter entity to update or add.
   * @param data The array of filter options under that entity. */
  public setCurrentFilterOptions(entity: string, data: ISelectable[]): void {
    const arr = this.filterOptions.find(a => a.entity === entity);
    if (arr) {
      arr.data = data;
    } else {
      this.filterOptions.push({ entity, data });
    }
  }
  /** Retrieves the filter options data and selected states for a given filter entity.
   *
   * @param entity The name of the filter entity to find.
   * @returns The data array as `ISelectable[]` (and selected states) of the filter entity if found; empty array otherwise.
   */
  public getCurrentFilterOptions(entity: string): any[] {
    const d = this.filterOptions.find(a => a.entity === entity);
    return d ? d.data : [];
  }
  public getFilterOptionsSelectedCount(entity: string): Observable<number> {
    return of(this.getCurrentFilterOptions(entity).filter(a => a.selected).length);
  }

  /** Clears the gallery sidebar filter options data. */
  public clearCurrentFilterOptions(): void {
    this.filterOptions = [];
    this.currentKeywordFilter = '';
    this.currentRelatedSetNameFilter = '';
    this.currentRelatedSetUIDFilter = 0;
  }

  public storeKeywordFilter(keyword: LU_Keyword | string): void {
    if (typeof (keyword) === 'string') {
      this.currentKeywordFilter = keyword;
    } else {
      this.currentKeywordFilter = keyword.keyword;
    }
  }
  public getKeywordFilter(): string {
    return this.currentKeywordFilter;
  }

  public storeRelatedSetNameFilter(rsv: string, uid?: number): void {
    this.currentRelatedSetNameFilter = rsv;
    if (uid !== undefined) {
      this.currentRelatedSetUIDFilter = uid;
    }
  }

  public getRelatedSetNameFilter(): string {
    return this.currentRelatedSetNameFilter;
  }

  get relatedSetUIDFilterActiveBool(): boolean {
    const cf = this.currentLibraryFilters;
    return cf && cf.filters && cf.filters.some(f => f[0] === 'RelatedSetUID');
  }

  get relatedSetNameFilterActiveUID(): number {
    const cf = this.currentLibraryFilters;
    if (!cf || !cf.filters) return 0;
    
    const matchedFilter = cf.filters.find((f: any[]) => f[0] === 'RelatedSetUID');
    if (matchedFilter && matchedFilter.length > 1) {
      return parseInt(matchedFilter[1], 10);
    }
    return 0;
  }

  public storeRelatedSetUIDFilter(uid: number): void {
    this.currentRelatedSetUIDFilter = uid;
  }

  public getRelatedSetUIDFilter(): number {
    return this.currentRelatedSetUIDFilter;
  }

  public clearRelatedSetFilters(): void {
    this.currentRelatedSetNameFilter = '';
    this.currentRelatedSetUIDFilter = 0;

    if (this.currentLibraryFilters && this.currentLibraryFilters.filters) {
      this.currentLibraryFilters.filters = this.currentLibraryFilters.filters.filter(
        f => f[0] !== 'RelatedSetUID'  // Changed from 'UID' or whatever it was
      );
    }
  }

  public getAllLibraryImages() {
    return this.libraryImages;
  }

  public getViewMode() {
    return this.viewMode;
  }

  public setViewMode(mode: string) {
    this.viewMode = mode;
  }

  public setLastSelectedRecord(imageFile: ImageFile) {
    this.lastViewedRecord = imageFile;
  }

  public getLastSelectedRecord() {
    return this.lastViewedRecord;
  }
  
  public setLastViewedRecordRelatedSetStatus(status: boolean) {
    this.lastViewedRecordIsRelatedResult = status;
  }

  public getLastViewedRecordRelatedSetStatus(): boolean {
    return this.lastViewedRecordIsRelatedResult;
  }

  public getLibraryRelatedSetImages(rppOverride = 35): ImageFile[] {
    const rpp = rppOverride ? rppOverride : this.currentLibraryFilters.rpp ?? 35;
    return this.libraryRelatedSetImages;
  }

  public setLibraryRelatedSetImages(lib: ImageFile[]): void {
    this.libraryRelatedSetImages = lib;
  }

  public clearLibraryRelatedSetImages(): void {
    this.libraryRelatedSetImages = [];
  }

  /** Returns the `CallNumberModel` currently in effect on the Search.
   * If there is not one, a new `CallNumberModel` instance will be constructed using the current gallery ImageType (or View All).
   */
  public getCurrentCallNoSearch(): CallNumberModel {
    if (this.currentCallNoSearch == null) {
      this.currentCallNoSearch = new CallNumberModel();
    }
    return new CallNumberModel().deserialize(this.currentCallNoSearch) as CallNumberModel;
  }

  /** Stores the `CallNumberModel` in effect for the search. */
  public setCurrentCallNoSearch(callNo: CallNumberModel): void {
    this.currentCallNoSearch = callNo;
  }

  private storeGalleryState(state: { type: ImageType; prePrinted: boolean; civResearch: boolean; ledgerSets: boolean }): void {
    try {
      localStorage.setItem(GALLERY_IMG_TYPE, JSON.stringify(state));
    } catch (e) {
      console.error('Could not write to LocalStorage. ', e);
    }
  }
  private retrieveGalleryState(): { type: ImageType; prePrinted: boolean; civResearch: boolean; ledgerSets: boolean } {
    try {
      const state = JSON.parse(localStorage.getItem(GALLERY_IMG_TYPE)) as { type: ImageType; prePrinted: boolean; civResearch: boolean; ledgerSets: boolean };
      // eslint-disable-next-line eqeqeq
      if (state.prePrinted == undefined) {
        state.prePrinted = false;
      }
      // eslint-disable-next-line eqeqeq
      if (state.civResearch == undefined) {
        state.civResearch = false;
      }
      if (state.ledgerSets == undefined) {
        state.ledgerSets = false;
      }
      return state;
    } catch (e) {
      console.error('Could not parse data from LocalStorage. ', e);
      this.galleryType = new ImageType();
    }
  }

  private PrePrintedMode(val: boolean): void {
    if (val === true) {
      if (this.getGalleryType().uid === 0 || this.getGalleryType().typeOfImage === 'All') {
        this._prePrintedMode = true;
        this._civilianResearchMode = false;
        this._ledgerSetsMode = false;
        const filters = this.getCurrentLibraryFilters(null, true);
        filters.rpp = DEFAULT_RPP_SETTING;
        if (this.currentLibraryFilters && !this.prePrintedMode) {
          filters.replaceFilter('TypeOfImageId', '0');
          filters.removeFromFilter('TypeOfImageId', '0');
          this.setCurrentLibraryFilters(filters);
          this.clearCurrentFilterOptions();
        }
      } else {
        throw new Error('Gallery cannot enter pre-printed military forms mode when not viewing ALL libraries.');
      }
    } else {
      this._prePrintedMode = false;
      const filters = this.getCurrentLibraryFilters(null, true);
      filters.rpp = DEFAULT_RPP_SETTING;
      if (this.currentLibraryFilters && !this.CivilianResearchMode) {
        filters.replaceFilter('TypeOfImageId', '0');
        filters.removeFromFilter('TypeOfImageId', '0');
        this.setCurrentLibraryFilters(filters);
        this.clearCurrentFilterOptions();
      }
    }
  }

  public get individualSearchTrigger$(): Subject<string> {
    return this._runIndividualSearchTrigger$;
  }

  public triggerIndividualSearch(searchText: string) {
    this._runIndividualSearchTrigger$.next(searchText);
  }

  public checkLibraryImages() {
    if (this._ledgerSetsMode) {
      return;
    }
    
    const expectedImages = this.currentLibraryFilters.page === this.currentLibraryFilters.totalPages ?
                              this.currentLibraryFilters.totalRecords :
                              this.currentLibraryFilters.rpp * this.currentLibraryFilters.page;

    if (this.libraryImages.length !== expectedImages && this.libraryImages.length > 0) {
      const params = new FilterParams<ImageFile>();

      params.collection = [];
      params.exactMatch = this.currentLibraryFilters.exactMatch;
      params.explicitSearchProperties = this.currentLibraryFilters.explicitSearchProperties;
      params.filters = this.currentLibraryFilters.filters;
      params.groupedRecordTotals = this.currentLibraryFilters.groupedRecordTotals;
      params.orClusterFilters = this.currentLibraryFilters.orClusterFilters;
      params.page = 1;
      params.rpp = expectedImages;
      params.searchFilters = this.currentLibraryFilters.searchFilters;
      params.searchPhrase = this.currentLibraryFilters.searchPhrase;
      params.sortAsc = this.currentLibraryFilters.sortAsc;
      params.sortFields = this.currentLibraryFilters.sortFields;
      params.sortStringNumerically = this.currentLibraryFilters.sortStringNumerically;
      params.spaceSeparatedSearch = this.currentLibraryFilters.spaceSeparatedSearch;

      this.repairLibraryImages.emit(params);
    }
  }

  public libraryRepairCompleted() {
    this.libraryImagesRepaired.emit();
  }
}
